<template>
  <b-modal
    ref="modal"
    title="Create a form with AI"
    size="lg"
    :no-close-on-backdrop="generating"
    :no-close-on-esc="generating"
    hide-header-close
    @hidden="onHidden"
  >
    <div class="ai-generate-body">
      <p class="ai-generate-intro">{{ introText }}</p>

      <label v-if="templates.length" class="ai-field-label" for="ai-template">Start from</label>
      <select v-if="templates.length" id="ai-template" v-model="templateId" :disabled="generating" class="ai-template-select">
        <option value="">A blank form (start from scratch)</option>
        <option v-for="t in templates" :key="t.FormIDForWeb" :value="t.FormIDForWeb">{{ t.Name }}</option>
      </select>

      <label class="ai-field-label" :class="{ 'mt-3': templates.length }" for="ai-form-prompt">{{ promptLabel }}</label>
      <div class="ai-prompt-wrap">
        <b-form-textarea
          id="ai-form-prompt"
          v-model="prompt"
          :disabled="generating"
          rows="5"
          max-rows="10"
          :placeholder="promptPlaceholder"
        />
        <button
          type="button"
          class="ai-mic-btn"
          :class="{ 'is-listening': listening }"
          :disabled="generating || !speechSupported"
          :title="micTitle"
          @click="toggleMic"
        >
          <i class="fa" :class="listening ? 'fa-stop' : 'fa-microphone'" />
        </button>
      </div>
      <div class="ai-prompt-meta">
        <span v-if="listening" class="ai-listening"><span class="ai-listening-dot" /> Listening…</span>
        <span :class="{ 'text-danger': prompt.length > MAX_LEN }">{{ prompt.length }}/{{ MAX_LEN }}</span>
      </div>

      <div v-if="generating" class="ai-generating">
        <b-spinner small class="mr-2" />
        Generating your form… this can take up to a minute.
      </div>
    </div>

    <div slot="modal-footer" class="w-100 d-flex justify-content-end">
      <b-button variant="outline-secondary" class="mr-2" :disabled="generating" @click="hide">Cancel</b-button>
      <b-button variant="primary" :disabled="!canGenerate" @click="generate">
        <i class="fa fa-magic mr-1" /> Generate
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { extractApiError } from '@/utils/apiError';

const MIN_LEN = 10;
const MAX_LEN = 2000;

/**
 * Assemble the prompt text during a dictation session: the text that already existed
 * before dictation started (baseText), plus the accumulated final transcripts and the
 * current interim (in-progress) transcript. Capped to maxLen.
 *
 * `results` is a normalized array of { isFinal: boolean, transcript: string } — the
 * caller flattens the browser's SpeechRecognitionResultList into this shape.
 * KEEP IN SYNC with cypress/node-tests/ai-generate-form.test.js (mirror).
 */
function buildDictatedPrompt(baseText, results, maxLen) {
  let finalT = '';
  let interimT = '';
  for (const r of results) {
    if (r.isFinal) finalT += r.transcript + ' ';
    else interimT += r.transcript;
  }
  return (baseText + finalT + interimT).slice(0, maxLen);
}

export default {
  name: 'AiGenerateFormModal',
  props: {
    siteId: { type: String, required: true },
    // Optional: the site's forms, used as "start from a template" options.
    templates: { type: Array, default: () => [] },
  },
  data() {
    return {
      prompt: '',
      generating: false,
      listening: false,
      speechSupported: false,
      templateId: '',
      MAX_LEN,
    };
  },
  computed: {
    isTemplateMode() { return !!this.templateId; },
    canGenerate() {
      const len = this.prompt.trim().length;
      const min = this.isTemplateMode ? 3 : MIN_LEN;
      return !this.generating && len >= min && len <= MAX_LEN;
    },
    introText() {
      return this.isTemplateMode
        ? 'Start from an existing form and describe the changes. AI builds a new draft to review before saving — your original form is untouched.'
        : 'Describe the form you want in plain English — type it or use the mic to dictate. AI will build a draft that opens in the builder for you to review before saving.';
    },
    promptLabel() { return this.isTemplateMode ? 'What should change?' : 'What should this form collect?'; },
    promptPlaceholder() {
      return this.isTemplateMode
        ? 'e.g. Keep everything but remove the insurance section, add an emergency contact name and phone, and make Date of Birth required.'
        : 'e.g. New patient intake form with first name, last name, date of birth, phone, email, an insurance card upload, and a signature. File the PDF under Signed Consent and put uploaded images under Photos.';
    },
    micTitle() {
      if (!this.speechSupported) return 'Voice input isn\'t supported in this browser (try Chrome or Edge)';
      return this.listening ? 'Stop dictation' : 'Dictate your description';
    },
  },
  mounted() {
    this.speechSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  },
  beforeDestroy() {
    this.stopListening();
  },
  methods: {
    ...mapActions('forms', ['generateForm', 'getForm', 'editForm']),

    show() {
      this.prompt = '';
      this.generating = false;
      this.templateId = '';
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    onHidden() {
      this.stopListening();
      this.prompt = '';
      this.generating = false;
      this.templateId = '';
    },

    // ── Voice dictation (browser Web Speech API) ──────────────────────────
    toggleMic() {
      if (this.generating || !this.speechSupported) return;
      if (this.listening) {
        this.stopListening();
      } else {
        this.startListening();
      }
    },
    startListening() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) return;
      const rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;

      // Anchor: append dictation after whatever is already in the box.
      this._baseText = this.prompt && !/\s$/.test(this.prompt) ? `${this.prompt} ` : this.prompt;

      rec.onresult = (event) => {
        const results = [];
        for (let i = 0; i < event.results.length; i++) {
          results.push({
            isFinal: event.results[i].isFinal,
            transcript: event.results[i][0].transcript,
          });
        }
        this.prompt = buildDictatedPrompt(this._baseText, results, MAX_LEN);
      };
      rec.onerror = (event) => {
        // Surface the raw code so we can diagnose; Web Speech API codes are terse.
        console.warn('[voice] SpeechRecognition error:', event.error, event);
        const code = event.error;
        if (code === 'not-allowed' || code === 'service-not-allowed') {
          this.$toast.error('Microphone access is blocked. Allow the mic for this site in your browser settings, then try again.');
        } else if (code === 'audio-capture') {
          this.$toast.error('No microphone was found. Check that a mic is connected and not in use by another app.');
        } else if (code === 'network') {
          this.$toast.error('Voice recognition couldn\'t reach the browser\'s speech service (network/firewall). Try a different network, or type your description instead.');
        } else if (code !== 'no-speech' && code !== 'aborted') {
          this.$toast.error(`Voice input error: ${code}. Please try again or type instead.`);
        }
        this.stopListening();
      };
      rec.onend = () => {
        this.listening = false;
      };

      this._recognition = rec;
      this.listening = true;
      try {
        rec.start();
      } catch (e) {
        // start() throws if called while already started — treat as a no-op.
        this.listening = false;
      }
    },
    stopListening() {
      if (this._recognition) {
        try { this._recognition.stop(); } catch (e) { /* already stopped */ }
        this._recognition = null;
      }
      this.listening = false;
    },

    // Unwrap a forms-service response: body may be the payload directly or nested under .data.
    _unwrap(response) {
      let body = response && response.data ? response.data : {};
      if (body && body.data && body.data.configData) body = body.data;
      return body;
    },

    async _fromScratch() {
      const response = await this.generateForm({ siteID: this.siteId, prompt: this.prompt.trim(), formType: 'OTHER' });
      return this._unwrap(response);
    },

    // Load the chosen template's ConfigData, then apply the instruction as edits.
    async _fromTemplate() {
      const tplRes = await this.getForm({ formIDForWeb: this.templateId });
      const cfgStr = tplRes && tplRes.data && tplRes.data.config && tplRes.data.config.ConfigData;
      if (!cfgStr) throw new Error('Could not load the selected form.');
      const baseConfig = JSON.parse(cfgStr);
      const tpl = this.templates.find((t) => t.FormIDForWeb === this.templateId) || {};
      const response = await this.editForm({ siteID: this.siteId, instruction: this.prompt.trim(), configData: baseConfig });
      const body = this._unwrap(response);
      body.suggestedName = tpl.Name ? `${tpl.Name} (AI edit)` : ((baseConfig.formConfig && baseConfig.formConfig.headline) || 'New Form');
      body.suggestedFormType = tpl.FormType || 'OTHER';
      return body;
    },

    async generate() {
      if (!this.canGenerate) return;
      this.stopListening();
      this.generating = true;
      try {
        const body = this.isTemplateMode ? await this._fromTemplate() : await this._fromScratch();

        if (!body || !body.configData) {
          throw new Error('The server did not return a form.');
        }

        // Stash the unsaved draft and hand off to the builder in the SAME TAB
        // (a new tab would drop this in-memory Vuex state).
        this.$store.commit('forms/setAiDraft', {
          configData: body.configData,
          suggestedName: body.suggestedName,
          suggestedFormType: body.suggestedFormType || 'OTHER',
          warnings: body.warnings || [],
          setupReport: body.setupReport || null,
        });

        this.$refs.modal.hide();
        this.$router.push({ path: 'forms/create-form' }).catch(() => {});
      } catch (err) {
        const status = err && err.response && err.response.status;
        let msg = 'Something went wrong generating the form. Please try again.';
        if (status === 400) {
          msg = extractApiError(err, 'Please add a bit more detail to your description.');
        } else if (status === 422) {
          msg = 'AI could not turn that into a form. Try rephrasing with the specific fields you want.';
        } else if (status === 502) {
          msg = 'The AI service is busy right now. Please try again in a moment.';
        }
        this.$toast.error(msg);
        this.generating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-generate-intro {
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.ai-field-label {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #475569;
  margin-bottom: 6px;
  display: block;
}

.ai-template-select {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #334155;
  background: #fff;
  margin-bottom: 4px;
}
.ai-template-select:focus { outline: none; border-color: #1a9e76; box-shadow: 0 0 0 3px rgba(26,158,118,.12); }

.ai-prompt-wrap {
  position: relative;
}

.ai-mic-btn {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  color: #475569;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #e2e8f0;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.is-listening {
    background: #ef4444;
    border-color: #ef4444;
    color: #fff;
    animation: ai-mic-pulse 1.3s ease-in-out infinite;
  }
}

@keyframes ai-mic-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
  70%  { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

.ai-prompt-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 4px;
}

.ai-listening {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ef4444;
  font-weight: 600;
}

.ai-listening-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: ai-mic-pulse 1.3s ease-in-out infinite;
}

.ai-generating {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  color: #475569;
  font-size: 0.9rem;
}
</style>
